import { Link, useNavigate } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import {
  CalendarDays,
  CircleArrowLeft,
  Copy,
  Loader2,
  Pencil,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useGetAllClassesWithSearch } from '~entities/classes';
import type { ClassSearchDetails } from '~entities/classes/modal/class-search';
import {
  invalidatePrivateLessonQueries,
  type PreviewPrivateLessonSeriesResponse,
  type PrivateLessonSlot,
  type SeriesBookSyncResult,
  useBookPrivateLesson,
  useBookPrivateLessonSeries,
  useCancelPrivateLessonBooking,
  useCancelPrivateLessonSeries,
  useGetPrivateLessonBookings,
  useGetPrivateLessonSeriesJob,
  useGetPrivateLessonSlots,
  usePreviewPrivateLessonSeries,
} from '~entities/private-lessons/api/private-lessons';
import type { StudentSearch } from '~entities/student';
import { useCurrentStudio } from '~entities/studios/model/studio-store';

import { StudentSelector } from '~features/students/ui/student-selector';

import {
  EnrollioRowActionMenu,
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { formatTrialDate } from '~shared/helpers/format-trial-datetime';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Label } from '~shared/shadcn/ui/label';
import { Progress } from '~shared/shadcn/ui/progress';
import { RadioGroup, RadioGroupItem } from '~shared/shadcn/ui/radio-group';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~shared/shadcn/ui/tabs';

import { CopyOfferingDialog } from './copy-offering-dialog';
import {
  findFirstBookableDate,
  formatPrivateLessonSlotTime,
  getAllowedDayIndexes,
  getApiErrorMessage,
  getPrivateLessonDisplayTimezone,
  isRetryableBookingFailure,
} from './private-lesson-booking-helpers';
import { PrivateLessonDatePicker } from './private-lesson-date-picker';
import {
  createDefaultRecurrenceState,
  PrivateLessonRecurrenceFields,
  PrivateLessonSeriesPreview,
  PrivateLessonSeriesResult,
  type SeriesRecurrenceFormState,
} from './private-lesson-recurrence-fields';
import { PrivateLessonSlotPicker } from './private-lesson-slot-picker';

const getOfferingStudentName = (student: unknown) => {
  if (!student || typeof student === 'string') return '';
  const s = student as { firstName?: string; lastName?: string; name?: string };
  return s.name ?? [s.firstName, s.lastName].filter(Boolean).join(' ');
};

const getOfferingTitle = (offering: unknown) => {
  if (!offering || typeof offering === 'string') return 'Private lesson';
  return (offering as { title?: string }).title ?? 'Private lesson';
};
const offeringColumnHelper = createColumnHelper<ClassSearchDetails>();

const BookPrivateLessonDialog = ({
  offering,
  open,
  onOpenChange,
}: {
  offering: ClassSearchDetails | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const studioTimezone = useCurrentStudio()?.timezone;
  const allowedDayIndexes = useMemo(
    () => getAllowedDayIndexes(offering),
    [offering],
  );
  const [bookingMode, setBookingMode] = useState<'single' | 'series'>('single');
  const [selectedStudent, setSelectedStudent] = useState<StudentSearch | null>(
    null,
  );
  const [date, setDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState<PrivateLessonSlot | null>(
    null,
  );
  const [paymentMode, setPaymentMode] = useState<'pay_later' | 'pay_now'>(
    'pay_later',
  );
  const [recurrence, setRecurrence] = useState<SeriesRecurrenceFormState>(
    createDefaultRecurrenceState(offering),
  );
  const [confirmSeriesOpen, setConfirmSeriesOpen] = useState(false);
  const [seriesPreview, setSeriesPreview] =
    useState<PreviewPrivateLessonSeriesResponse | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [seriesOutcome, setSeriesOutcome] = useState<{
    seriesId: string;
    booked: number;
    skipped: SeriesBookSyncResult['skipped'];
    failed: SeriesBookSyncResult['failed'];
  } | null>(null);
  /** Lessons booked by earlier attempts, carried across an async retry job. */
  const bookedCarryOverRef = useRef(0);

  useEffect(() => {
    if (!open) {
      setBookingMode('single');
      setSelectedStudent(null);
      setDate('');
      setSelectedSlot(null);
      setPaymentMode('pay_later');
      setRecurrence(createDefaultRecurrenceState(offering));
      setConfirmSeriesOpen(false);
      setSeriesPreview(null);
      setActiveJobId(null);
      setSeriesOutcome(null);
      return;
    }

    if (!offering) return;

    setDate(findFirstBookableDate(offering, allowedDayIndexes));
    setSelectedSlot(null);
    setRecurrence(createDefaultRecurrenceState(offering));
    setConfirmSeriesOpen(false);
    setSeriesPreview(null);
    setActiveJobId(null);
    setSeriesOutcome(null);
  }, [open, offering, allowedDayIndexes]);

  const slotsQuery = useGetPrivateLessonSlots(
    {
      offeringId: offering?._id ?? '',
      startDate: date,
      endDate: date,
    },
    { enabled: open && !!offering?._id && !!date && bookingMode === 'single' },
  );

  const seriesAnchorDate =
    bookingMode === 'series'
      ? recurrence.frequency === 'custom'
        ? (recurrence.customDates[0] ?? date)
        : recurrence.startDate
      : date;

  const seriesSlotsQuery = useGetPrivateLessonSlots(
    {
      offeringId: offering?._id ?? '',
      startDate: seriesAnchorDate,
      endDate: seriesAnchorDate,
    },
    {
      enabled:
        open &&
        !!offering?._id &&
        !!seriesAnchorDate &&
        bookingMode === 'series',
    },
  );

  const bookMutation = useBookPrivateLesson();
  const previewMutation = usePreviewPrivateLessonSeries();
  const bookSeriesMutation = useBookPrivateLessonSeries();
  const jobQuery = useGetPrivateLessonSeriesJob(activeJobId, {
    enabled: !!activeJobId,
    refetchInterval: activeJobId ? 2000 : undefined,
  });

  const slots = slotsQuery.data?.slots ?? [];
  const seriesSlots = seriesSlotsQuery.data?.slots ?? [];
  const slotTimezone =
    slotsQuery.data?.timezone ??
    seriesSlotsQuery.data?.timezone ??
    studioTimezone ??
    undefined;

  /**
   * Keeps the confirm dialog open when some occurrences failed so the dates and
   * reasons stay visible and can be retried into the same series.
   */
  const finishSeriesBooking = useCallback(
    (result: SeriesBookSyncResult, alreadyBooked = 0) => {
      invalidatePrivateLessonQueries();
      const bookedTotal = alreadyBooked + result.booked.length;
      // On a retry the skipped list is just the lessons booked by the first
      // attempt coming back as conflicts, so reporting it would be misleading.
      const skipped = alreadyBooked > 0 ? [] : result.skipped;

      if (result.failed.length > 0) {
        setSeriesOutcome({
          seriesId: result.seriesId,
          booked: bookedTotal,
          skipped,
          failed: result.failed,
        });
        const canRetry = result.failed.some((item) =>
          isRetryableBookingFailure(item.reason),
        );
        const summary = `Booked ${bookedTotal} lessons · ${result.failed.length} failed`;
        if (canRetry) {
          toast.warning(summary);
        } else {
          toast.error(`${summary}. These dates cannot be booked as scheduled.`);
        }
        return;
      }

      setSeriesOutcome(null);
      toast.success(
        `Booked ${bookedTotal} lessons` +
          (skipped.length ? ` (${skipped.length} skipped)` : ''),
      );
      setConfirmSeriesOpen(false);
      onOpenChange(false);
    },
    [onOpenChange],
  );

  useEffect(() => {
    if (!jobQuery.data) return;
    if (
      jobQuery.data.status === 'completed' ||
      jobQuery.data.status === 'failed'
    ) {
      const result = jobQuery.data.result;
      if (jobQuery.data.status === 'completed' && result) {
        finishSeriesBooking(result, bookedCarryOverRef.current);
      } else if (jobQuery.data.status === 'failed') {
        toast.error(jobQuery.data.errorMessage ?? 'Series booking job failed');
        invalidatePrivateLessonQueries();
      }
      setActiveJobId(null);
    }
  }, [jobQuery.data, finishSeriesBooking]);

  const buildSeriesPayload = () => {
    if (!offering || !selectedStudent) return null;

    const recurrenceRule =
      recurrence.frequency === 'custom'
        ? {
            frequency: recurrence.frequency,
            startDate: recurrence.startDate,
            customDates: recurrence.customDates,
            anchorStartTime: selectedSlot?.startTime,
          }
        : {
            frequency: recurrence.frequency,
            startDate: recurrence.startDate,
            endDate:
              recurrence.endMode === 'date' ? recurrence.endDate : undefined,
            occurrenceCount:
              recurrence.endMode === 'count'
                ? Number(recurrence.occurrenceCount)
                : undefined,
            dayOfWeek: recurrence.daysOfWeek[0],
            daysOfWeek: recurrence.daysOfWeek,
            anchorStartTime: selectedSlot?.startTime,
          };

    return {
      offeringId: offering._id,
      studentId: selectedStudent._id,
      seriesId: seriesPreview?.seriesId,
      recurrence: recurrenceRule,
      paymentMode,
      skipConflicts: true,
    };
  };

  const handleBookSingle = async () => {
    if (!offering || !selectedStudent || !selectedSlot) {
      toast.error('Select a student and time slot');
      return;
    }

    try {
      await bookMutation.mutateAsync({
        offeringId: offering._id,
        studentId: selectedStudent._id,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        paymentMode,
      });
      toast.success('Private lesson booked');
      onOpenChange(false);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to book private lesson'));
    }
  };

  const handleOpenSeriesConfirm = async () => {
    const payload = buildSeriesPayload();
    if (!payload || !selectedSlot) {
      toast.error('Select a student and anchor time slot');
      return;
    }
    if (
      recurrence.frequency === 'custom' &&
      recurrence.customDates.length === 0
    ) {
      toast.error('Add at least one custom date');
      return;
    }

    setSeriesPreview(null);
    setSeriesOutcome(null);
    setConfirmSeriesOpen(true);

    try {
      const preview = await previewMutation.mutateAsync(payload);
      setSeriesPreview(preview);
    } catch (error: unknown) {
      setConfirmSeriesOpen(false);
      toast.error(getApiErrorMessage(error, 'Failed to load series preview'));
    }
  };

  const runSeriesBooking = async (seriesId: string, alreadyBooked: number) => {
    const payload = buildSeriesPayload();
    if (!payload || !selectedSlot) {
      toast.error('Select a student and anchor time slot');
      return;
    }

    try {
      const result = await bookSeriesMutation.mutateAsync({
        ...payload,
        seriesId,
      });
      if (result.async) {
        bookedCarryOverRef.current = alreadyBooked;
        setActiveJobId(result.jobId);
        toast.message(`Booking ${result.totalToBook} lessons...`);
        return;
      }

      finishSeriesBooking(result, alreadyBooked);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to book series'));
    }
  };

  const handleConfirmBookSeries = async () => {
    if (!seriesPreview) {
      toast.error('Select a student and anchor time slot');
      return;
    }
    await runSeriesBooking(seriesPreview.seriesId, 0);
  };

  /**
   * Re-sends the original series payload under the same seriesId. Occurrences
   * that already booked come back as conflicts and are skipped, so only the
   * previously failed dates are attempted again.
   */
  const handleRetryFailedSeries = async () => {
    if (!seriesOutcome) return;
    await runSeriesBooking(seriesOutcome.seriesId, seriesOutcome.booked);
  };

  const isSubmitting =
    bookMutation.isPending ||
    previewMutation.isPending ||
    bookSeriesMutation.isPending ||
    !!activeJobId;

  const canBookSeries =
    !!selectedStudent &&
    !!selectedSlot &&
    (recurrence.frequency === 'custom'
      ? recurrence.customDates.length > 0
      : recurrence.daysOfWeek.length > 0);

  const retryableFailures =
    seriesOutcome?.failed.filter((item) =>
      isRetryableBookingFailure(item.reason),
    ) ?? [];
  const previewAvailable = seriesPreview?.summary.available ?? 0;

  const jobProgress =
    jobQuery.data && jobQuery.data.totalRecords > 0
      ? Math.round(
          (jobQuery.data.processedRecords / jobQuery.data.totalRecords) * 100,
        )
      : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] max-w-3xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Book Private Lesson</DialogTitle>
        </DialogHeader>

        <div className='space-y-4'>
          <div>
            <p className='font-medium'>{offering?.title}</p>
            <p className='text-sm text-muted-foreground'>
              {offering?.duration ?? 0} minutes lesson
              {offering?.maxSize && offering.maxSize > 1
                ? ` · up to ${offering.maxSize} students per slot`
                : ''}
            </p>
          </div>

          <div className='space-y-2'>
            <Label>Student</Label>
            <StudentSelector
              placeholder='Select student'
              className='w-full'
              onChange={(student) => {
                setSelectedStudent(student ?? null);
              }}
            />
          </div>

          <Tabs
            value={bookingMode}
            onValueChange={(value) => {
              setBookingMode(value as 'single' | 'series');
            }}
          >
            <TabsList className='grid w-full grid-cols-2'>
              <TabsTrigger value='single'>Single lesson</TabsTrigger>
              <TabsTrigger value='series'>Series</TabsTrigger>
            </TabsList>

            <TabsContent value='single' className='space-y-4'>
              <PrivateLessonDatePicker
                offering={offering}
                date={date}
                onDateChange={(nextDate) => {
                  setDate(nextDate);
                  setSelectedSlot(null);
                }}
              />

              <div className='space-y-2'>
                <Label>Available slots</Label>
                {slotsQuery.isFetching ? (
                  <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                    <Loader2 className='size-4 animate-spin' />
                    Loading slots...
                  </div>
                ) : slotsQuery.isError ? (
                  <p className='rounded-md border border-destructive p-3 text-sm text-destructive'>
                    Failed to load available slots.
                  </p>
                ) : (
                  <PrivateLessonSlotPicker
                    slots={slots}
                    selectedSlot={selectedSlot}
                    onSelect={setSelectedSlot}
                    timezone={slotTimezone}
                  />
                )}
              </div>
            </TabsContent>

            <TabsContent value='series' className='space-y-4'>
              <PrivateLessonRecurrenceFields
                offering={offering}
                value={recurrence}
                onChange={setRecurrence}
              />

              <div className='space-y-2'>
                <Label>Anchor time slot</Label>
                {seriesSlotsQuery.isFetching ? (
                  <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                    <Loader2 className='size-4 animate-spin' />
                    Loading slots...
                  </div>
                ) : (
                  <PrivateLessonSlotPicker
                    slots={seriesSlots}
                    selectedSlot={selectedSlot}
                    onSelect={setSelectedSlot}
                    timezone={slotTimezone}
                  />
                )}
                <p className='text-xs text-muted-foreground'>
                  This time applies to every date in the series.
                </p>
              </div>
            </TabsContent>
          </Tabs>

          <div className='space-y-2'>
            <Label>Payment</Label>
            <RadioGroup
              value={paymentMode}
              onValueChange={(value) => {
                setPaymentMode(value as 'pay_later' | 'pay_now');
              }}
              className='grid grid-cols-1 gap-2 md:grid-cols-2'
            >
              <Label className='flex cursor-pointer items-center gap-2 rounded-md border p-3'>
                <RadioGroupItem value='pay_later' />
                Pay later - create pending invoice per lesson
              </Label>
              <Label className='flex cursor-pointer items-center gap-2 rounded-md border p-3'>
                <RadioGroupItem value='pay_now' />
                Pay now - charge parent&apos;s saved card
              </Label>
            </RadioGroup>
          </div>
        </div>

        <DialogFooter className='gap-2 sm:gap-0'>
          <Button
            type='button'
            variant='outline'
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          {bookingMode === 'single' ? (
            <Button
              type='button'
              disabled={isSubmitting}
              onClick={handleBookSingle}
            >
              {bookMutation.isPending ? 'Booking...' : 'Book lesson'}
            </Button>
          ) : (
            <Button
              type='button'
              disabled={isSubmitting || !canBookSeries}
              onClick={handleOpenSeriesConfirm}
            >
              {previewMutation.isPending ? 'Loading...' : 'Book series'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      <Dialog
        open={confirmSeriesOpen}
        onOpenChange={(nextOpen) => {
          if (isSubmitting) return;
          setConfirmSeriesOpen(nextOpen);
          if (!nextOpen) {
            setSeriesPreview(null);
            setSeriesOutcome(null);
          }
        }}
      >
        <DialogContent className='max-h-[90vh] max-w-2xl overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>
              {seriesOutcome
                ? 'Series booked with errors'
                : 'Confirm series booking'}
            </DialogTitle>
          </DialogHeader>

          <div className='space-y-4'>
            <div className='rounded-md border p-3 text-sm'>
              <p className='font-medium'>{offering?.title}</p>
              <p className='text-muted-foreground'>
                {selectedStudent
                  ? `${selectedStudent.firstName} ${selectedStudent.lastName}`.trim()
                  : 'Student'}
                {selectedSlot
                  ? ` · ${formatPrivateLessonSlotTime(selectedSlot, slotTimezone)}`
                  : ''}
              </p>
              <p className='text-muted-foreground'>
                Payment:{' '}
                {paymentMode === 'pay_now'
                  ? 'Pay now (charge saved card per lesson)'
                  : 'Pay later (pending invoice per lesson)'}
              </p>
            </div>

            {seriesOutcome ? (
              <PrivateLessonSeriesResult
                booked={seriesOutcome.booked}
                skipped={seriesOutcome.skipped}
                failed={seriesOutcome.failed}
                canRetry={retryableFailures.length > 0}
                timezone={seriesPreview?.timezone ?? slotTimezone}
              />
            ) : previewMutation.isPending || !seriesPreview ? (
              <div className='flex items-center gap-2 py-8 text-sm text-muted-foreground'>
                <Loader2 className='size-4 animate-spin' />
                Loading series preview...
              </div>
            ) : (
              <PrivateLessonSeriesPreview
                occurrences={seriesPreview.occurrences}
                summary={seriesPreview.summary}
                timezone={seriesPreview.timezone}
                recurrenceLabel={seriesPreview.recurrenceLabel}
              />
            )}

            {activeJobId && (
              <div className='space-y-2'>
                <p className='text-sm text-muted-foreground'>
                  Booking lessons... {jobQuery.data?.processedRecords ?? 0} /{' '}
                  {jobQuery.data?.totalRecords ?? '?'}
                </p>
                <Progress value={jobProgress} />
              </div>
            )}
          </div>

          <DialogFooter className='gap-2 sm:gap-0'>
            <Button
              type='button'
              variant='outline'
              disabled={isSubmitting}
              onClick={() => {
                setConfirmSeriesOpen(false);
                setSeriesPreview(null);
                setSeriesOutcome(null);
                if (seriesOutcome) onOpenChange(false);
              }}
            >
              {seriesOutcome ? 'Done' : 'Back'}
            </Button>
            {seriesOutcome ? (
              retryableFailures.length > 0 && (
                <Button
                  type='button'
                  disabled={isSubmitting}
                  onClick={handleRetryFailedSeries}
                >
                  {bookSeriesMutation.isPending || activeJobId
                    ? 'Retrying...'
                    : `Retry ${retryableFailures.length} failed`}
                </Button>
              )
            ) : (
              <Button
                type='button'
                disabled={isSubmitting || previewAvailable === 0}
                onClick={handleConfirmBookSeries}
              >
                {bookSeriesMutation.isPending || activeJobId
                  ? 'Booking...'
                  : `Confirm ${previewAvailable} lessons`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export const PrivateLessonsPage = () => {
  const navigate = useNavigate();
  const studioTimezone = useCurrentStudio()?.timezone;
  const [selectedOffering, setSelectedOffering] =
    useState<ClassSearchDetails | null>(null);
  const [bookDialogOpen, setBookDialogOpen] = useState(false);
  const [selectedOfferingIds, setSelectedOfferingIds] = useState<Array<string>>(
    [],
  );
  const [copySources, setCopySources] = useState<
    Array<{ id: string; title: string }>
  >([]);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [seriesPendingCancel, setSeriesPendingCancel] = useState<{
    seriesId: string;
    studentName: string;
    offeringTitle: string;
    lessonCount?: number;
    recurrenceLabel?: string;
  } | null>(null);
  const { data, isFetching } = useGetAllClassesWithSearch({
    page: 1,
    limit: 100,
    studio: true,
    privateLessonsOnly: true,
  });
  const upcomingBookings = useGetPrivateLessonBookings({ timing: 'upcoming' });
  const pastBookings = useGetPrivateLessonBookings({ timing: 'past' });
  const cancelBookingMutation = useCancelPrivateLessonBooking();
  const cancelSeriesMutation = useCancelPrivateLessonSeries();

  const offerings = data?.data.classes ?? [];
  const allOfferingIds = offerings.map((offering) => offering._id);
  const allSelected =
    offerings.length > 0 &&
    selectedOfferingIds.length === offerings.length &&
    offerings.every((offering) => selectedOfferingIds.includes(offering._id));
  const someSelected = selectedOfferingIds.length > 0 && !allSelected;

  const openCopyDialog = (sources: Array<{ id: string; title: string }>) => {
    setCopySources(sources);
    setCopyDialogOpen(true);
  };

  const toggleOfferingSelected = (offeringId: string, checked: boolean) => {
    setSelectedOfferingIds((current) => {
      if (checked) {
        return current.includes(offeringId)
          ? current
          : [...current, offeringId];
      }
      return current.filter((id) => id !== offeringId);
    });
  };
  const bookingRows = useMemo(
    () => [
      {
        title: 'Upcoming bookings',
        rows: upcomingBookings.data?.bookings ?? [],
        timezone: upcomingBookings.data?.timezone,
        isLoading: upcomingBookings.isPending,
        isError: upcomingBookings.isError,
        canCancel: true,
      },
      {
        title: 'Past bookings',
        rows: pastBookings.data?.bookings ?? [],
        timezone: pastBookings.data?.timezone,
        isLoading: pastBookings.isPending,
        isError: pastBookings.isError,
        canCancel: false,
      },
    ],
    [
      upcomingBookings.data,
      upcomingBookings.isPending,
      upcomingBookings.isError,
      pastBookings.data,
      pastBookings.isPending,
      pastBookings.isError,
    ],
  );

  const seriesCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const booking of upcomingBookings.data?.bookings ?? []) {
      if (!booking.seriesId) continue;
      counts.set(booking.seriesId, (counts.get(booking.seriesId) ?? 0) + 1);
    }
    return counts;
  }, [upcomingBookings.data?.bookings]);

  const openBookDialog = (offering: ClassSearchDetails) => {
    setSelectedOffering(offering);
    setBookDialogOpen(true);
  };

  const handleCancelBooking = async (bookingId: string) => {
    try {
      await cancelBookingMutation.mutateAsync(bookingId);
      toast.success('Lesson cancelled');
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to cancel lesson'));
    }
  };

  const handleCancelSeries = async () => {
    if (!seriesPendingCancel) return;
    try {
      const result = await cancelSeriesMutation.mutateAsync(
        seriesPendingCancel.seriesId,
      );
      toast.success(`Cancelled ${result.cancelledCount} remaining lessons`);
      setSeriesPendingCancel(null);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to cancel series'));
    }
  };

  const offeringColumns = useMemo(
    () => [
      offeringColumnHelper.display({
        id: 'select',
        header: () => (
          <Checkbox
            aria-label='Select all offerings'
            checked={allSelected}
            indeterminate={someSelected}
            onCheckedChange={(value) => {
              setSelectedOfferingIds(value ? allOfferingIds : []);
            }}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            aria-label={`Select ${row.original.title}`}
            checked={selectedOfferingIds.includes(row.original._id)}
            onCheckedChange={(value) => {
              toggleOfferingSelected(row.original._id, !!value);
            }}
          />
        ),
      }),
      offeringColumnHelper.accessor('title', {
        header: 'Offering',
        cell: (info) => (
          <div>
            <div className='font-medium'>{info.getValue()}</div>
            <Badge variant='secondary'>Private lesson</Badge>
          </div>
        ),
      }),
      offeringColumnHelper.display({
        id: 'schedule',
        header: 'Schedule',
        cell: ({ row }) => (
          <div>
            <div>{row.original.days.join(', ')}</div>
            <div className='text-muted-foreground'>
              {row.original.startTime} - {row.original.endTime}
            </div>
          </div>
        ),
      }),
      offeringColumnHelper.accessor('tuitionFee', {
        header: 'Tuition',
        cell: (info) => `$${String(info.getValue())}`,
      }),
      offeringColumnHelper.accessor('maxSize', {
        header: 'Capacity',
        cell: (info) =>
          info.getValue() && info.getValue() > 1
            ? `${String(info.getValue())} / slot`
            : '1 / slot',
      }),
      offeringColumnHelper.accessor('duration', {
        header: 'Class duration',
        cell: (info) => `${String(info.getValue())} min`,
      }),
      offeringColumnHelper.accessor(
        (offering) => offering.slotInterval ?? offering.duration,
        {
          id: 'slotInterval',
          header: 'Slot interval',
          cell: (info) => `${String(info.getValue())} min`,
        },
      ),
      offeringColumnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <EnrollioRowActionMenu
            label={`Actions for ${row.original.title}`}
            actions={[
              {
                id: 'book',
                label: 'Book',
                icon: CalendarDays,
                onSelect: () => {
                  openBookDialog(row.original);
                },
              },
              {
                id: 'copy',
                label: 'Copy offering',
                icon: Copy,
                onSelect: () => {
                  openCopyDialog([
                    { id: row.original._id, title: row.original.title },
                  ]);
                },
              },
              {
                id: 'edit',
                label: 'Edit',
                icon: Pencil,
                onSelect: () => {
                  void navigate({
                    to: '/classes/$classId/update',
                    params: { classId: row.original._id },
                  });
                },
              },
            ]}
          />
        ),
      }),
    ],
    [
      allOfferingIds,
      allSelected,
      navigate,
      selectedOfferingIds,
      someSelected,
    ],
  );

  return (
    <div className='space-y-6'>
      <Link to='/classes' className='flex w-max items-center gap-2'>
        <CircleArrowLeft className='size-6' />
        <h2 className='text-2xl font-semibold'>Private Lessons</h2>
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>Offerings</CardTitle>
        </CardHeader>
        <CardContent>
          <EnrollioTable
            totalLabel='Private lesson offerings'
            filtersLayout='inline'
            filterHeader={(table) => (
              <EnrollioTableSearch
                table={table}
                placeholder='Search offerings...'
              />
            )}
            toolbar={
              <div className='flex items-center gap-2'>
                {selectedOfferingIds.length > 0 && (
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => {
                      openCopyDialog(
                        offerings
                          .filter((offering) =>
                            selectedOfferingIds.includes(offering._id),
                          )
                          .map((offering) => ({
                            id: offering._id,
                            title: offering.title,
                          })),
                      );
                    }}
                  >
                    {selectedOfferingIds.length === 1
                      ? 'Copy offering'
                      : `Copy ${selectedOfferingIds.length} offerings`}
                  </Button>
                )}
                <Button asChild>
                  <Link to='/classes/new'>Create offering</Link>
                </Button>
              </div>
            }
            data={offerings}
            columns={offeringColumns}
            getRowId={(row) => row._id}
            isUpdating={isFetching}
            emptyContent='No private lesson offerings yet. Create a class and enable the private lesson offering toggle.'
            ariaLabel='Private lesson offerings'
          />
        </CardContent>
      </Card>

      {bookingRows.map((section) => (
        <Card key={section.title}>
          <CardHeader>
            <CardTitle>{section.title}</CardTitle>
          </CardHeader>
          <CardContent>
            {section.isLoading ? (
              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                <Loader2 className='size-4 animate-spin' />
                Loading bookings...
              </div>
            ) : section.isError ? (
              <p className='text-sm text-destructive'>
                Failed to load bookings. Please try again.
              </p>
            ) : section.rows.length === 0 ? (
              <p className='text-sm text-muted-foreground'>
                No bookings found.
              </p>
            ) : (
              <div className='space-y-2'>
                {section.rows.map((booking) => {
                  const displayTimezone = getPrivateLessonDisplayTimezone(
                    booking,
                    section.timezone,
                    studioTimezone,
                  );
                  const seriesCount = booking.seriesId
                    ? seriesCounts.get(booking.seriesId)
                    : undefined;
                  const seriesId = booking.seriesId;

                  return (
                    <div
                      key={booking._id}
                      className='flex flex-wrap items-center justify-between gap-3 rounded-md border p-3'
                    >
                      <div>
                        <p className='font-medium'>
                          {getOfferingStudentName(booking.studentId) ||
                            'Unknown Student'}
                        </p>
                        <p className='text-sm text-muted-foreground'>
                          {getOfferingTitle(booking.offeringId)} ·{' '}
                          {formatTrialDate(
                            booking.startTime,
                            displayTimezone,
                            'MMM d, yyyy',
                          ) ?? '-'}{' '}
                          ·{' '}
                          {formatPrivateLessonSlotTime(
                            booking,
                            displayTimezone,
                          )}
                        </p>
                        {seriesId && (
                          <div className='mt-1 flex flex-wrap items-center gap-2'>
                            <Badge variant='outline'>
                              Series
                              {seriesCount ? ` · ${seriesCount} lessons` : ''}
                            </Badge>
                            {booking.recurrenceLabel && (
                              <span className='text-xs text-muted-foreground'>
                                {booking.recurrenceLabel}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className='flex flex-wrap items-center gap-2'>
                        <Badge
                          variant={
                            booking.paymentStatus === 'paid'
                              ? 'default'
                              : 'secondary'
                          }
                        >
                          {booking.paymentStatus}
                        </Badge>
                        {section.canCancel && (
                          <>
                            <Button
                              type='button'
                              size='sm'
                              variant='outline'
                              disabled={cancelBookingMutation.isPending}
                              onClick={() => handleCancelBooking(booking._id)}
                            >
                              Cancel lesson
                            </Button>
                            {seriesId && (
                              <Button
                                type='button'
                                size='sm'
                                variant='destructive'
                                disabled={cancelSeriesMutation.isPending}
                                onClick={() => {
                                  setSeriesPendingCancel({
                                    seriesId,
                                    studentName:
                                      getOfferingStudentName(
                                        booking.studentId,
                                      ) || 'Unknown Student',
                                    offeringTitle: getOfferingTitle(
                                      booking.offeringId,
                                    ),
                                    lessonCount: seriesCount,
                                    recurrenceLabel: booking.recurrenceLabel,
                                  });
                                }}
                              >
                                Cancel series
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      <CopyOfferingDialog
        open={copyDialogOpen}
        onOpenChange={setCopyDialogOpen}
        sources={copySources}
        onCopied={() => {
          setSelectedOfferingIds([]);
        }}
      />
      <BookPrivateLessonDialog
        offering={selectedOffering}
        open={bookDialogOpen}
        onOpenChange={setBookDialogOpen}
      />

      <Dialog
        open={seriesPendingCancel !== null}
        onOpenChange={(nextOpen) => {
          if (cancelSeriesMutation.isPending) return;
          if (!nextOpen) setSeriesPendingCancel(null);
        }}
      >
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>Cancel this series?</DialogTitle>
            <DialogDescription>
              {seriesPendingCancel?.lessonCount
                ? `All ${seriesPendingCancel.lessonCount} remaining lessons in this series will be cancelled. This cannot be undone.`
                : 'All remaining lessons in this series will be cancelled. This cannot be undone.'}
            </DialogDescription>
          </DialogHeader>

          {seriesPendingCancel && (
            <div className='rounded-md border p-3 text-sm'>
              <p className='font-medium'>{seriesPendingCancel.studentName}</p>
              <p className='text-muted-foreground'>
                {seriesPendingCancel.offeringTitle}
              </p>
              {seriesPendingCancel.recurrenceLabel && (
                <p className='text-muted-foreground'>
                  {seriesPendingCancel.recurrenceLabel}
                </p>
              )}
            </div>
          )}

          <DialogFooter className='gap-2 sm:gap-0'>
            <Button
              type='button'
              variant='outline'
              disabled={cancelSeriesMutation.isPending}
              onClick={() => {
                setSeriesPendingCancel(null);
              }}
            >
              Keep series
            </Button>
            <Button
              type='button'
              variant='destructive'
              disabled={cancelSeriesMutation.isPending}
              onClick={handleCancelSeries}
            >
              {cancelSeriesMutation.isPending
                ? 'Cancelling...'
                : 'Cancel series'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
